import { ActionContext, Module } from 'vuex';
import { getUserCredit } from '@/api/homeV2Status';
import type { UserCreditType } from '@/api/homeV2Status/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { creditTaskStatusTypeEnum } from '@/enums/autarkyUpgrade';
import type { Store } from 'vuex';
import { StateType } from './types';
import RootStateType from '../types';
import generateMutations, { UPDATE_STATE_TYPE } from '../generate-mutations';

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    userCredit: undefined,
    isProgressingTask: undefined,
    isHangAndProgressingTask: undefined,
  },
  actions: {
    /** 读取用户授信状态数据 */
    async getUserCredit(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<UserCreditType> {
      /** 请求新数据 */
      const userCreditData = await getUserCredit();

      if (!userCreditData.businessData) {
        ubtSendError(new Error('userCreditData.businessData is null'));
        return userCreditData;
      }

      context.dispatch(UPDATE_STATE_TYPE, {
        userCredit: userCreditData.businessData,
        isProgressingTask: userCreditData.businessData.progressingTaskId,
        isHangAndProgressingTask: userCreditData.businessData.progressingTaskId
          && userCreditData.businessData.creditTaskStatus === creditTaskStatusTypeEnum.HANG,
      });

      return userCreditData;
    },

    /** 检查用户是否有进行中的授信任务 */
    async checkHasProgressingTask(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        await context.dispatch('getUserCredit');

        return !!context.state.isProgressingTask;
      } catch (error) {
        ubtSendError(error as Error);
        /** 若是接口异常，默认返回 false，中断流程 */
        return false;
      }
    },

    /**
     * 检查用户是否有进行中的授信任务，且任务挂起状态（自营授信中，联营还未提交申请）
     */
    async checkHasHangAndProgressingTask(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        await context.dispatch('getUserCredit');

        return !!context.state.isHangAndProgressingTask;
      } catch (error) {
        ubtSendError(error as Error);
        /** 若是接口异常，默认返回 false，中断流程 */
        return false;
      }
    },
  },
};

export default generateMutations(moduleStore);

export const generateDispatchActions = (store: Store<RootStateType>): {
  getUserCredit: () => Promise<UserCreditType>;
  checkHasProgressingTask: () => Promise<boolean>;
  checkHasHangAndProgressingTask: () => Promise<boolean>;
} => ({
  getUserCredit:
    (): Promise<UserCreditType> => store.dispatch('userCreditStatus/getUserCredit') as unknown as Promise<UserCreditType>,
  checkHasProgressingTask:
    (): Promise<boolean> => store.dispatch('userCreditStatus/checkHasProgressingTask') as unknown as Promise<boolean>,
  checkHasHangAndProgressingTask:
    (): Promise<boolean> => store.dispatch('userCreditStatus/checkHasHangAndProgressingTask') as unknown as Promise<boolean>,
});
